import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ThumbsUp, MessageSquare } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function QATab({ event, user }) {
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState('');
  const [reply, setReply] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/questions`);
      setQuestions(r.data || []);
    } catch (err) {
      console.error('Failed to load questions:', err);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/events/${event.id}/questions`, { text: text.trim() });
      setText('');
      load();
    } catch (err) {
      console.error('Failed to post question:', err);
      toast.error('Failed to post question');
    }
  };
  const upvote = async (q) => {
    try {
      await api.post(`/questions/${q.id}/upvote`);
      load();
    } catch (err) {
      console.error('Failed to upvote:', err);
    }
  };
  const answer = async (q) => {
    const t = (reply[q.id] || '').trim();
    if (!t) return;
    try {
      await api.post(`/questions/${q.id}/answer`, { text: t });
      setReply((s) => ({ ...s, [q.id]: '' }));
      load();
    } catch (err) {
      console.error('Failed to answer:', err);
      toast.error('Failed to post reply');
    }
  };

  return (
    <div className="space-y-3" data-testid="qa-tab">
      <Card className="rounded-2xl p-3">
        <div className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask a question…" data-testid="qa-question-input" className="flex-1" />
          <Button onClick={submit} data-testid="qa-submit-button" className="rounded-xl gradient-fab text-white">Ask</Button>
        </div>
      </Card>
      {questions.length === 0 && <Card className="rounded-2xl p-6 text-center text-sm text-muted-foreground">No questions yet.</Card>}
      {questions.map((q) => (
        <Card key={q.id} className="rounded-2xl p-4" data-testid={`question-${q.id}`}>
          <div className="flex items-start gap-3">
            <button onClick={() => upvote(q)} data-testid={`upvote-${q.id}`} className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-xl ${q.upvoter_ids?.includes(user?.id) ? 'bg-primary/10 text-[hsl(var(--primary))]' : 'bg-secondary'}`}>
              <ThumbsUp className="h-4 w-4" /><span className="text-xs font-semibold">{q.upvotes || 0}</span>
            </button>
            <div className="flex-1">
              <div className="text-sm">{q.text}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{q.user_name} · {new Date(q.created_at).toLocaleString()}</div>
              {(q.answers || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {q.answers.map((a) => (
                    <div key={a.id} className="rounded-xl bg-secondary p-2 text-sm" data-testid={`answer-${a.id}`}>
                      <div className="text-[11px] text-muted-foreground mb-0.5">{a.user_name}</div>
                      <div>{a.text}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <Input value={reply[q.id] || ''} onChange={(e) => setReply((s) => ({ ...s, [q.id]: e.target.value }))} placeholder="Write a reply…" data-testid={`answer-input-${q.id}`} />
                <Button onClick={() => answer(q)} data-testid={`answer-button-${q.id}`} variant="outline" className="rounded-xl"><MessageSquare className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
